
import React from 'react';
import { User, Calendar, MapPin, Globe, Flag, Building, MailOpen, Phone, Lock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Patient } from '@/types/doctor';

interface PatientPersonalInfoProps {
  patient: Patient & {
    profile?: any;
  };
}

const PatientPersonalInfo: React.FC<PatientPersonalInfoProps> = ({ patient }) => {
  const profile = patient.profile || {};
  
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center">
            <User className="h-5 w-5 mr-2" />
            Personal Information
          </CardTitle>
          <CardDescription>Patient basic demographic data</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-start">
                <div className="w-32 text-muted-foreground">Full Name:</div>
                <div className="font-medium">{profile.name || patient.name}</div>
              </div>
              <div className="flex items-start">
                <div className="w-32 text-muted-foreground">Date of Birth:</div>
                <div className="font-medium">
                  {profile.dateOfBirth 
                    ? new Date(profile.dateOfBirth).toLocaleDateString() 
                    : 'Not provided'}
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-32 text-muted-foreground">Gender:</div>
                <div className="font-medium capitalize">
                  {profile.gender || 'Not provided'}
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-32 text-muted-foreground">Language:</div>
                <div className="font-medium">
                  {profile.preferredLanguage || 'Not specified'}
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-start">
                <div className="w-32 text-muted-foreground">Contact:</div>
                <div className="flex flex-col">
                  <div className="flex items-center text-muted-foreground">
                    <Lock className="h-3 w-3 mr-1" />
                    <span>Contact information hidden for privacy</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Patient contact details are protected for privacy reasons and only visible to admins
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center">
            <MapPin className="h-5 w-5 mr-2" />
            Address Information
          </CardTitle>
          <CardDescription>Patient location data</CardDescription>
        </CardHeader>
        <CardContent>
          {profile.address ? (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-start">
                  <div className="w-32 text-muted-foreground">
                    <Globe className="h-4 w-4 inline mr-1" />
                    Country:
                  </div>
                  <div className="font-medium">{profile.address.country}</div>
                </div>
                <div className="flex items-start">
                  <div className="w-32 text-muted-foreground">
                    <Flag className="h-4 w-4 inline mr-1" />
                    State:
                  </div>
                  <div className="font-medium">{profile.address.state}</div>
                </div>
                <div className="flex items-start">
                  <div className="w-32 text-muted-foreground">
                    <Building className="h-4 w-4 inline mr-1" />
                    City:
                  </div>
                  <div className="font-medium">{profile.address.city}</div>
                </div>
                <div className="flex items-start">
                  <div className="w-32 text-muted-foreground">Street:</div>
                  <div className="font-medium">{profile.address.street}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="py-4 text-center text-muted-foreground">
              No address information provided
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PatientPersonalInfo;
